import React from 'react';
import { Box, Typography, Card, Container, Grid } from '@mui/joy';
import { motion } from 'framer-motion';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloudIcon from '@mui/icons-material/Cloud';
import DataObjectIcon from '@mui/icons-material/DataObject';

const capabilities = [
  {
    icon: <PsychologyIcon />,
    title: "AI Strategy & Integration",
    description: "We identify where AI can create real business value and implement solutions that enhance your operations.",
    features: ["AI readiness assessment", "Strategic implementation roadmap", "Custom AI integrations", "ROI-focused solutions"]
  },
  {
    icon: <RocketLaunchIcon />,
    title: "Intelligent Automation",
    description: "Streamline operations with AI-powered automation that reduces costs and improves efficiency.",
    features: ["Process automation", "Intelligent document processing", "Smart workflow optimization", "Cost reduction analysis"]
  },
  {
    icon: <CloudIcon />,
    title: "Smart Infrastructure",
    description: "Build resilient systems enhanced with AI monitoring, predictive maintenance, and intelligent scaling.",
    features: ["Predictive monitoring", "Intelligent resource management", "Automated incident response", "Performance optimization"]
  },
  {
    icon: <DataObjectIcon />,
    title: "Business Intelligence+",
    description: "Transform your data into actionable insights with AI-enhanced analytics and decision support systems.",
    features: ["Predictive analytics", "Custom dashboards", "Automated reporting", "Decision support systems"]
  }
];

export const CapabilitiesSection = () => {
  return (
    <Box id="capabilities" sx={{ py: 12, background: 'rgba(15, 23, 42, 0.8)' }}>
      <Container maxWidth="xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              level="h2" 
              sx={{ 
                mb: 2,
                background: 'linear-gradient(135deg, #0B4BB3 0%, #00D4FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              How We Enhance Your Business with AI
            </Typography>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              We strategically integrate AI where it matters most – solving real problems and creating measurable value
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          {capabilities.map((capability, index) => (
            <Grid key={index} xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <Card
                  sx={{
                    p: 4,
                    height: '100%',
                    background: 'rgba(30, 41, 59, 0.4)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(100, 116, 139, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      border: '1px solid rgba(0, 212, 255, 0.4)',
                      boxShadow: '0 10px 40px rgba(0, 212, 255, 0.1)',
                    }
                  }}
                >
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #0B4BB3 0%, #00D4FF 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        '& svg': {
                          fontSize: '1.8rem',
                          color: 'white'
                        }
                      }}
                    >
                      {capability.icon}
                    </Box>
                    
                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                      {capability.title}
                    </Typography>
                    
                    <Typography level="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                      {capability.description}
                    </Typography>
                  </Box>

                  <Box>
                    {capability.features.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + featureIndex * 0.05 }}
                        viewport={{ once: true }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            mb: 1.5,
                            '&:last-child': { mb: 0 }
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: '#00FF88',
                              flexShrink: 0
                            }}
                          />
                          <Typography level="body2" sx={{ color: 'text.secondary' }}>
                            {feature}
                          </Typography>
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};